import { Box, Typography, useTheme } from "@mui/material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

export type ConsortiumMemberBadgeSize = "small" | "medium";

interface ConsortiumMemberBadgeProps {
  /** header 用は "small"、ページ内は "medium"（既定） */
  size?: ConsortiumMemberBadgeSize;
}

/**
 * コンソーシアム会員バッジ。
 *
 * 所属クライアントが共同コンソーシアム企業のときにのみ表示する「特別感」のある会員バッジ。
 * ゴールド系の上品なグラデーションと微光沢、王冠アイコンで会員特典感を出しつつ、
 * R.E. DATA Plus の落ち着いた業務UIに調和する範囲に留める。ライト/ダーク両対応。
 *
 * 表示可否（isConsortiumMember 判定）は呼び出し側で行う。
 */
export const ConsortiumMemberBadge: React.FC<ConsortiumMemberBadgeProps> = ({
  size = "medium",
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const compact = size === "small";

  // 上品なシャンパンゴールドのグラデーション（ライト/ダークで明度を調整）
  const gradient = isDark
    ? "linear-gradient(135deg, #7a5c17 0%, #b8912f 45%, #e8c766 100%)"
    : "linear-gradient(135deg, #b8860b 0%, #d9ab3c 45%, #f3d789 100%)";
  const textColor = isDark ? "#fdf6e3" : "#4a3608";
  const borderColor = isDark
    ? "rgba(243, 215, 137, 0.55)"
    : "rgba(154, 116, 12, 0.55)";

  return (
    <Box
      component="span"
      role="img"
      aria-label="コンソーシアム会員"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? 0.4 : 0.6,
        pl: compact ? 0.75 : 1,
        pr: compact ? 1 : 1.25,
        py: compact ? 0.25 : 0.4,
        borderRadius: 999,
        background: gradient,
        border: `1px solid ${borderColor}`,
        color: textColor,
        whiteSpace: "nowrap",
        // 微光沢と立体感
        boxShadow: isDark
          ? "0 1px 2px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.25)"
          : "0 1px 2px rgba(154,116,12,0.35), inset 0 1px 0 rgba(255,255,255,0.55)",
        // ほのかなハイライトの帯
        position: "relative",
        overflow: "hidden",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)",
          pointerEvents: "none",
        },
      }}
    >
      <WorkspacePremiumIcon
        sx={{
          fontSize: compact ? 15 : 18,
          color: textColor,
          filter: "drop-shadow(0 0.5px 0.5px rgba(0,0,0,0.25))",
        }}
      />
      <Typography
        component="span"
        sx={{
          fontSize: compact ? 11 : 12.5,
          fontWeight: 700,
          letterSpacing: "0.02em",
          lineHeight: 1,
        }}
      >
        コンソーシアム会員
      </Typography>
    </Box>
  );
};
